// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import {
  Children,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
} from 'react'
import { DetailsLoadingShimmer } from '@teamnine/pcj-ui-v2'

import { useLayout } from '@/app/shared/components/Layout/layout-context'
import PageAppBar from './AppBar'
import * as Styled from './Styles'

export type PageProps = {
  documentTitle?: string
  isFullScreen?: boolean
  children: ReactNode | ReactNode[]
}

export default function Page({
  documentTitle,
  isFullScreen = false,
  children,
}: PageProps) {
  const {
    isNavCollapsed,
    isFullScreen: layoutIsFullScreen,
    setIsFullScreen,
  } = useLayout({ ignoreError: true })

  useEffect(() => {
    if (documentTitle) {
      document.title = documentTitle
    }
  }, [documentTitle])

  useEffect(() => {
    if (!setIsFullScreen || !isFullScreen) {
      return
    }

    setIsFullScreen(true)
    return () => {
      setIsFullScreen(false)
    }
  }, [isFullScreen, setIsFullScreen])

  return (
    <Styled.Display
      $isCollapsed={isNavCollapsed}
      $isFullScreen={layoutIsFullScreen}
    >
      {Children.map(children, (child) =>
        isValidElement(child) ? cloneElement(child, { ...child.props }) : null
      )}
    </Styled.Display>
  )
}

export type PageContentProps = {
  className?: string
  children: ReactNode | ReactNode[]
  isLoading?: boolean
  isAuthor?: boolean
  authorSize?: 'default' | 'large'
  isFullScreen?: boolean
  backgroundColor?: string
}

const PageContent = ({
  className,
  isLoading,
  children,
  isFullScreen,
}: PageContentProps) => {
  const content = useMemo(() => {
    if (isLoading) return <DetailsLoadingShimmer />
    return children
  }, [children, isLoading])

  return (
    <Styled.Content className={className} $isFullScreen={isFullScreen ?? false}>
      {content}
    </Styled.Content>
  )
}

Page.AppBar = PageAppBar
Page.Content = PageContent
